import { message } from 'antd';
import { useCallback, useEffect, useState } from 'react';

import {
  approveArtifact,
  getArtifact,
  updateArtifact,
  type TranscriptAnalysisArtifactResponseDTO,
  type ValidationSummaryDTO,
} from '@/api/assessments/api';

export type {
  TranscriptAnalysisArtifactResponseDTO,
  ValidationSummaryDTO,
} from '@/api/assessments/api';

export interface UseAssessmentStep2Result {
  artifact: TranscriptAnalysisArtifactResponseDTO | null;
  validation: ValidationSummaryDTO | null;
  loading: boolean;
  error: string | null;
  editMode: boolean;
  editValue: string;
  saving: boolean;
  approving: boolean;
  setEditMode: (value: boolean) => void;
  setEditValue: (value: string) => void;
  handleEdit: () => void;
  handleSave: () => Promise<void>;
  handleApprove: () => Promise<void>;
}

/**
 * Manages fetch, edit, save, and approval state for the transcript-analysis review step.
 * The Milkdown editor ref stays in the component; markdown changes are wired back via
 * `setEditValue` so `handleSave` always has the current value.
 */
export function useAssessmentStep2(
  assessmentInstanceId: string,
  accessToken: string | null,
  clientId: string,
  projectId: string,
  onNavigateForward?: () => void,
): UseAssessmentStep2Result {
  const [artifact, setArtifact] = useState<TranscriptAnalysisArtifactResponseDTO | null>(null);
  const [validation, setValidation] = useState<ValidationSummaryDTO | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editMode, setEditMode] = useState(false);
  const [editValue, setEditValue] = useState('');
  const [saving, setSaving] = useState(false);
  const [approving, setApproving] = useState(false);

  useEffect(() => {
    if (!accessToken) {
      return;
    }
    setLoading(true);
    getArtifact(assessmentInstanceId, accessToken, clientId, projectId)
      .then((data) => {
        setArtifact(data);
        setValidation(data.validation);
        setEditValue(data.markdown);
      })
      .catch(() => setError('Failed to load the analysis artifact.'))
      .finally(() => setLoading(false));
  }, [assessmentInstanceId, accessToken, clientId, projectId]);

  const handleEdit = useCallback(() => {
    setEditValue(artifact?.markdown ?? '');
    setEditMode(true);
  }, [artifact]);

  const handleSave = useCallback(async () => {
    if (!accessToken) {
      return;
    }
    setSaving(true);
    try {
      const updated = await updateArtifact(
        assessmentInstanceId,
        editValue,
        accessToken,
        clientId,
        projectId,
      );
      setArtifact((prev) =>
        prev ? { ...prev, markdown: updated.markdown, revision: updated.revision } : prev,
      );
      setValidation(updated.validation);
      setEditMode(false);
    } catch {
      void message.error('Failed to save changes. Please try again.');
    } finally {
      setSaving(false);
    }
  }, [assessmentInstanceId, accessToken, clientId, projectId, editValue]);

  const handleApprove = useCallback(async () => {
    if (!accessToken) {
      return;
    }
    setApproving(true);
    try {
      const result = await approveArtifact(assessmentInstanceId, accessToken, clientId, projectId);
      setArtifact((prev) =>
        prev
          ? { ...prev, approval_state: 'approved', approved_revision: result.approved_revision }
          : prev,
      );
      onNavigateForward?.();
    } catch {
      void message.error('Failed to approve. Please try again.');
    } finally {
      setApproving(false);
    }
  }, [assessmentInstanceId, accessToken, clientId, projectId, onNavigateForward]);

  return {
    artifact,
    validation,
    loading,
    error,
    editMode,
    editValue,
    saving,
    approving,
    setEditMode,
    setEditValue,
    handleEdit,
    handleSave,
    handleApprove,
  };
}
